/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getIcepacksFilledSelector,
	getIcepacksFillersOptionsSelector,
	getIcepacksStatesOptionsSelector,
	getIsIcepacksFilledLoadingSelector,
	getIsIcepacksFillersLoadingSelector,
	getIsIcepacksStatesLoadingSelector,
	getIsModesLoadingSelector,
	getModesOptionsSelector,
	getPaginationSelector,
} from './selectors';
import {
	getIcepacksFilledAction,
	getIcepacksFillersAction,
	getIcepacksStatesAction,
	getModesAction,
	resetStateAction,
} from './actions';
import { getIsAvailableIcepacksFilledButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { NAV_PAGE_LIST, NAV_PAGE_LIST_ICEPACKS } from '../icepacks/constants';

const IcepacksFilledContainerWrapper = ({
	onGetIcepacksFilled,
	icepacksFilled,
	isIcepacksFilledLoading,
	pagination,
	onResetState,
	isAvailable,
	onGetIcepacksFillers,
	onGetIcepacksStates,
	onGetModes,
	icepacksFillersOptions,
	isIcepacksFillersLoading,
	icepacksStatesOptions,
	isIcepacksStatesLoading,
	modesOptions,
	isModesLoading,
}) => {
	useInjectSaga({ key: 'icepacksFilledContainerSaga', saga });
	useInjectReducer({ key: 'icepacksFilledContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetIcepacksFilled({ searchQuery: removeModalQuery(query.toString()) });
		onGetIcepacksFillers();
		onGetIcepacksStates();
		onGetModes();

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'icepack_filled');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);

		history.push(`${pathname}?${query}`);

		onGetIcepacksFilled({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);

		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}

		history.push(`${pathname}?${query}`);

		onGetIcepacksFilled({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);

		onGetIcepacksFilled({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(
				normalizeDataGridModel(
					filterModel,
					TABLE_COLUMNS(icepacksFillersOptions, icepacksStatesOptions, modesOptions),
				),
			);

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);

			onGetIcepacksFilled({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={NAV_PAGE_LIST}
			pageContent={
				<PageContentWithNav
					pageNavigationItems={NAV_PAGE_LIST_ICEPACKS}
					isInnerRouter
					pageContent={
						<DataGridComponent
							headerText="Хладоэлементы с наполнителями"
							columns={TABLE_COLUMNS(icepacksFillersOptions, icepacksStatesOptions, modesOptions)}
							style={pagination ? { height: 'calc(100vh - 344px)' } : { height: 'calc(100vh - 287px)' }}
							onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
							rows={icepacksFilled}
							pagination={
								!pagination
									? null
									: {
											...pagination,
											onPageChange: handlePageChange,
											onSelectChange: handleSelectChange,
									  }
							}
							isTableDataLoading={
								isIcepacksFilledLoading ||
								isIcepacksFillersLoading ||
								isIcepacksStatesLoading ||
								isModesLoading
							}
							handleSortChange={handleSortChange}
							handleFilterChange={handleFilterChange}
						/>
					}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	icepacksFilled: getIcepacksFilledSelector(),
	isIcepacksFilledLoading: getIsIcepacksFilledLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableIcepacksFilledButtonsSelector(),
	icepacksFillersOptions: getIcepacksFillersOptionsSelector(),
	isIcepacksFillersLoading: getIsIcepacksFillersLoadingSelector(),
	icepacksStatesOptions: getIcepacksStatesOptionsSelector(),
	isIcepacksStatesLoading: getIsIcepacksStatesLoadingSelector(),
	modesOptions: getModesOptionsSelector(),
	isModesLoading: getIsModesLoadingSelector(),
});

const mapDispatchToProps = {
	onGetIcepacksFilled: getIcepacksFilledAction,
	onResetState: resetStateAction,
	onGetIcepacksFillers: getIcepacksFillersAction,
	onGetIcepacksStates: getIcepacksStatesAction,
	onGetModes: getModesAction,
};

const IcepacksFilledContainer = connect(mapStateToProps, mapDispatchToProps)(IcepacksFilledContainerWrapper);

IcepacksFilledContainerWrapper.propTypes = {
	onGetIcepacksFilled: PropTypes.func.isRequired,
	icepacksFilled: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string,
			link: PropTypes.string.isRequired,
		}),
	),
	isIcepacksFilledLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	onGetIcepacksFillers: PropTypes.func.isRequired,
	onGetIcepacksStates: PropTypes.func.isRequired,
	onGetModes: PropTypes.func.isRequired,
	icepacksFillersOptions: PropTypes.array,
	isIcepacksFillersLoading: PropTypes.bool.isRequired,
	icepacksStatesOptions: PropTypes.array,
	isIcepacksStatesLoading: PropTypes.bool.isRequired,
	modesOptions: PropTypes.array,
	isModesLoading: PropTypes.bool.isRequired,
};

export default IcepacksFilledContainer;
